import type { Instance, ProviderId } from './catalog'

/**
 * How each managed Kubernetes service carves a node up before your pods get any of
 * it: how many pods it lets the node run, and what it reserves for the kubelet, the
 * container runtime, the OS and the eviction threshold.
 *
 * Every number here comes from the provider's own documentation or node bootstrap
 * code, cited per rule. CPU is millicores, memory MiB.
 */

export interface Resources {
  readonly cpu: number
  readonly memory: number
}

export interface NodeProfile {
  /** Upper bound on pods per node, before any DaemonSet takes a slot. */
  readonly maxPods: number
  /** `kube-reserved` plus `system-reserved`: never schedulable. */
  readonly reserved: Resources
  /** The kubelet's hard `memory.available` eviction threshold. */
  readonly evictionMemory: number
}

/**
 * The one setting per provider that changes the answer. On EKS, AKS and OKE it is
 * the pod networking mode, which decides how many pods fit; on GKE it is the
 * version the node pool was created on, which decides the reservation formula.
 */
export const PROVIDER_OPTIONS = {
  aws: ['vpc-cni', 'prefix-delegation'],
  gcp: ['gke-1-36', 'gke-1-37'],
  azure: ['overlay', 'node-subnet', 'kubenet'],
  oci: ['vcn-native', 'flannel'],
} as const satisfies Record<ProviderId, readonly string[]>

export type ProviderOption<P extends ProviderId = ProviderId> = (typeof PROVIDER_OPTIONS)[P][number]

export type ProviderOptions = { readonly [P in ProviderId]: ProviderOption<P> }

export const DEFAULT_OPTIONS: ProviderOptions = {
  aws: 'vpc-cni',
  // 1.37 is in the Rapid channel only; Regular and Stable create 1.36 node pools.
  gcp: 'gke-1-36',
  // The network plugin `az aks create` uses when none is given.
  azure: 'overlay',
  // What the OKE console's quick-create workflow sets up.
  oci: 'vcn-native',
}

export function isProviderOption<P extends ProviderId>(
  provider: P,
  value: unknown,
): value is ProviderOption<P> {
  return (PROVIDER_OPTIONS[provider] as readonly unknown[]).includes(value)
}

/** The kubelet's default hard eviction threshold, which EKS, GKE, AKS and OKE all keep. */
const EVICTION_MEMORY = 100

/** The kubelet's default `--max-pods`. */
const KUBELET_MAX_PODS = 110

/** Applies a percentage per band, like a tax table. `bands` are [upper bound, rate]. */
function banded(amount: number, bands: readonly (readonly [number, number])[]): number {
  let total = 0
  let lower = 0
  for (const [upper, rate] of bands) {
    if (amount <= lower) break
    total += (Math.min(amount, upper) - lower) * rate
    lower = upper
  }
  return total
}

/** Piecewise-linear interpolation through [x, y] points, held flat beyond either end. */
function interpolate(x: number, points: readonly (readonly [number, number])[]): number {
  const first = points[0]
  const last = points[points.length - 1]
  if (!first || !last) return 0
  if (x <= first[0]) return first[1]
  for (let i = 1; i < points.length; i++) {
    const [x0, y0] = points[i - 1] ?? first
    const [x1, y1] = points[i] ?? last
    if (x <= x1) return y0 + ((x - x0) * (y1 - y0)) / (x1 - x0)
  }
  return last[1]
}

/** 6% of the first core, 1% of the second, 0.5% of the next two, 0.25% of the rest. */
const CPU_BANDS = [
  [1000, 0.06],
  [2000, 0.01],
  [4000, 0.005],
  [Infinity, 0.0025],
] as const

const capacityOf = (instance: Instance): Resources => ({
  cpu: instance.vcpu * 1000,
  memory: instance.memoryGiB * 1024,
})

// ------------------------------------------------------------------ EKS

/**
 * Pods per node with the Amazon VPC CNI: every pod takes a secondary IP on one of
 * the node's ENIs, each ENI's primary IP is not handed out, and two host-network
 * pods (aws-node, kube-proxy) need none.
 * https://github.com/awslabs/amazon-eks-ami/blob/main/templates/shared/runtime/eni-max-pods.txt
 *
 * With prefix delegation each slot holds a /28 (16 addresses) instead, and EKS caps
 * the result at 110 below 30 vCPUs and 250 above. Only Nitro instances support it.
 * https://docs.aws.amazon.com/eks/latest/userguide/cni-increase-ip-addresses.html
 */
export function eksMaxPods(instance: Instance, option: ProviderOption<'aws'>): number {
  const enis = instance.enis ?? 0
  const slots = Math.max(0, (instance.ipv4PerEni ?? 0) - 1)
  if (option === 'prefix-delegation' && instance.nitro) {
    return Math.min(enis * slots * 16 + 2, instance.vcpu < 30 ? 110 : 250)
  }
  return enis * slots + 2
}

/**
 * The EKS AMI's kubelet config (nodeadm on AL2023, bootstrap.sh before it): memory
 * 255 MiB plus 11 MiB per pod the node can run; CPU on the shared banded scale,
 * rounded down per band as the shell arithmetic does.
 */
function eksProfile(instance: Instance, option: ProviderOption<'aws'>): NodeProfile {
  const maxPods = eksMaxPods(instance, option)
  const cores = instance.vcpu * 1000
  const cpu = CPU_BANDS.reduce((sum, [upper, rate], i) => {
    const lower = i === 0 ? 0 : (CPU_BANDS[i - 1]?.[0] ?? 0)
    return sum + Math.floor(Math.max(0, Math.min(cores, upper) - lower) * rate)
  }, 0)
  return {
    maxPods,
    reserved: { cpu, memory: 255 + 11 * maxPods },
    evictionMemory: EVICTION_MEMORY,
  }
}

// ------------------------------------------------------------------ GKE

/**
 * GKE Standard node pools.
 * https://cloud.google.com/kubernetes-engine/docs/concepts/plan-node-sizes
 *
 * Pools created on 1.36 or earlier reserve a regressive share of memory (255 MiB
 * below 1 GiB). From 1.37 the reservation is the lower of that and 15 MiB per pod
 * plus 500 MiB (Container-Optimized OS), and CPU is capped at one core. Shared-core
 * E2 types reserve a flat 1060m of their two burstable vCPUs.
 */
function gkeProfile(instance: Instance, option: ProviderOption<'gcp'>): NodeProfile {
  const { cpu: cores, memory } = capacityOf(instance)
  const maxPods = KUBELET_MAX_PODS
  let reservedMemory =
    memory < 1024
      ? 255
      : banded(memory, [
          [4096, 0.25],
          [8192, 0.2],
          [16384, 0.1],
          [131072, 0.06],
          [Infinity, 0.02],
        ])
  let reservedCpu = instance.sharedCore ? 1060 : banded(cores, CPU_BANDS)
  if (option === 'gke-1-37') {
    reservedMemory = Math.min(reservedMemory, 15 * maxPods + 500)
    if (!instance.sharedCore) reservedCpu = Math.min(reservedCpu, 1000)
  }
  return {
    maxPods,
    reserved: { cpu: reservedCpu, memory: reservedMemory },
    evictionMemory: EVICTION_MEMORY,
  }
}

// ------------------------------------------------------------------ AKS

/**
 * Default max pods by network plugin: Azure CNI Overlay 250, Azure CNI with pods on
 * the node subnet 30, kubenet 110.
 * https://learn.microsoft.com/azure/aks/concepts-network-ip-address-planning#maximum-pods-per-node
 */
const AKS_MAX_PODS: Record<ProviderOption<'azure'>, number> = {
  overlay: 250,
  'node-subnet': 30,
  kubenet: 110,
}

/**
 * AKS 1.29 and later: memory is the lesser of 20 MiB per pod plus 50 MiB and a
 * quarter of the node. CPU follows Microsoft's table — 60m for one core, 100m for
 * two, 140m for four, then 10m per further core (180m at 8, 740m at 64) — which is
 * 40m for the second core and 20m each for the third and fourth.
 * https://learn.microsoft.com/azure/aks/node-resource-reservations
 */
function aksProfile(instance: Instance, option: ProviderOption<'azure'>): NodeProfile {
  const { memory } = capacityOf(instance)
  const maxPods = AKS_MAX_PODS[option]
  const cpu = interpolate(instance.vcpu, [
    [1, 60],
    [2, 100],
    [4, 140],
    // The table stops at 64 cores; the 10m-per-core slope carries on past it.
    [1024, 140 + 10 * (1024 - 4)],
  ])
  return {
    maxPods,
    reserved: { cpu, memory: Math.min(20 * maxPods + 50, memory * 0.25) },
    evictionMemory: EVICTION_MEMORY,
  }
}

// ------------------------------------------------------------------ OKE

/**
 * VCN-native pod networking gives each secondary VNIC 31 pod IPs, the primary VNIC
 * none, and OKE caps a node at 110 pods. Flannel is an overlay, so only the
 * kubelet's default limit applies.
 * https://docs.oracle.com/iaas/Content/ContEng/Concepts/contengpodnetworking_topic-OCI_CNI_plugin.htm
 */
export function okeMaxPods(instance: Instance, option: ProviderOption<'oci'>): number {
  if (option === 'flannel') return KUBELET_MAX_PODS
  return Math.min(Math.max(0, (instance.vnics ?? 0) - 1) * 31, KUBELET_MAX_PODS)
}

/**
 * OKE images since June 2024 apply Oracle's recommended reservations by default.
 * Oracle publishes them as a table rather than a formula, so between rows this
 * interpolates linearly; past the last row the table's own rule continues (2.5m per
 * core, 20 MiB per GiB). On top: a flat 100m and 100 MiB of `system-reserved`.
 * https://docs.oracle.com/iaas/Content/ContEng/Tasks/contengtroubleshooting_topic-node_issues.htm
 */
function okeProfile(instance: Instance, option: ProviderOption<'oci'>): NodeProfile {
  const cores = instance.vcpu
  const gib = instance.memoryGiB
  const kubeCpu =
    cores <= 5
      ? interpolate(cores, [
          [1, 60],
          [2, 70],
          [3, 80],
          [4, 85],
          [5, 90],
        ])
      : 90 + 2.5 * (cores - 5)
  const kubeMemoryGiB =
    gib <= 128
      ? interpolate(gib, [
          [8, 1],
          [16, 2],
          [128, 9],
        ])
      : 9 + ((gib - 128) * 20) / 1024
  return {
    maxPods: okeMaxPods(instance, option),
    reserved: { cpu: kubeCpu + 100, memory: kubeMemoryGiB * 1024 + 100 },
    evictionMemory: EVICTION_MEMORY,
  }
}

export function nodeProfile(instance: Instance, options: ProviderOptions): NodeProfile {
  switch (instance.provider) {
    case 'aws':
      return eksProfile(instance, options.aws)
    case 'gcp':
      return gkeProfile(instance, options.gcp)
    case 'azure':
      return aksProfile(instance, options.azure)
    case 'oci':
      return okeProfile(instance, options.oci)
  }
}

export { capacityOf }
